import { ApiContentType, ApiHeaders, ApiMethod } from '@/types/api';
import { getXDeviceCode } from '@/utils/cookie';
import { serialize } from 'object-to-formdata';
import qs from 'qs';

type TBaseResponse<T> = {
    success: 1 | 0
    data: T
    message: string
}

type TPostApiHeaders = Partial<Omit<ApiHeaders, 'x-device-code'>>

const getApiHeaders = (headers?: TPostApiHeaders): Headers => {
    const h = new Headers();
    if (headers) {
        Object.keys(headers).forEach(key => {
            if (key === 'Content-Type') {
                if (headers[key] === ApiContentType.MultipartFormData) {
                    return;
                } else {
                    h.append(key, headers[key]!);
                }
            } else {
                // @ts-ignore
                h.append(key, headers[key] as string);
            }
        });
    } else {
        h.append('Content-Type', ApiContentType.ApplicationXWWWFormUrlencoded);
    }
    h.append('x-device-code', getXDeviceCode());
    return h;
};

export class BaseApi {
    private readonly baseApi: string = process.env.REACT_APP_BASE_API || '';

    constructor(path: string) {
        this.baseApi += path;
    }

    private getApiBody<TData>(contentType: ApiContentType, data?: TData): string | FormData | undefined {
        switch (contentType) {
            case ApiContentType.ApplicationJSON:
                return JSON.stringify(data);
            case ApiContentType.MultipartFormData:
                return serialize(data);
            case ApiContentType.ApplicationXWWWFormUrlencoded:
            default:
                return data ? new URLSearchParams(Object.entries(data)).toString() : undefined;
        }
    }

    protected async get<TResponse = void, TReq = {}>(url: string, data?: TReq): Promise<TResponse> {
        try {
            const res = await fetch(this.baseApi + url + qs.stringify(data, { addQueryPrefix: true }), {
                headers: {
                    'x-device-code': getXDeviceCode(),
                },
            });
            const json: TBaseResponse<TResponse> = await res.json();
            if (json.success) {
                return json.data;
            }
            throw new Error(json.message);
        } catch (e) {
            console.log(e);
            throw (e as Error).message;
        }
    }

    private async baseRequest<TRes, TReq extends {}>(method: ApiMethod, url: string, data?: TReq, headers?: TPostApiHeaders): Promise<TRes> {
        try {
            const res: Response = await fetch(this.baseApi + url, {
                body: this.getApiBody(headers && headers['Content-Type'] ? headers['Content-Type'] : ApiContentType.ApplicationXWWWFormUrlencoded, data),
                method: method,
                headers: getApiHeaders(headers),
            });
            const json: TBaseResponse<TRes> = await res.json();
            if (json.success) {
                return json.data;
            }
            throw new Error(json.message);
        } catch (e) {
            console.log(e);
            throw (e as Error).message;
        }
    }

    protected async post<TResponse, TRequest extends {}>(url: string, data?: TRequest, headers?: TPostApiHeaders): Promise<TResponse> {
        return await this.baseRequest(ApiMethod.Post, url, data, headers);
    }

    protected async delete<TRes, TReq extends {}>(url: string, data?: TReq, headers?: TPostApiHeaders): Promise<TRes> {
        return await this.baseRequest(ApiMethod.Delete, url, data, headers);
    }

    protected async patch<TRes, TReq extends {}>(url: string, data?: TReq, headers?: TPostApiHeaders): Promise<TRes> {
        return await this.baseRequest(ApiMethod.Patch, url, data, headers);
    }

    protected async put<TRes, TReq extends {}>(url: string, data?: TReq, headers?: TPostApiHeaders): Promise<TRes> {
        return await this.baseRequest(ApiMethod.Put, url, data, headers);
    }
}
